import { useLocalization } from '@lib/localization';
import {
  Box, CardContent, FormControl, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { usePermission } from '@store/hooks';
import { type FC, useEffect, useRef, useState } from 'react';
import { CardDescription, CardHeader, CardTitle } from './ServerConfig.styled';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;

/**
 * The range the API accepts, mirroring `sessionExpiresInSecondsMin`/`Max` in the backend's
 * `config.model.ts` — its zod schema rejects the PUT outside it. One pair for the whole
 * card: the previous per-field caps drifted apart from the server's, and a month counted
 * as 30 days, so the last five of the 365 allowed days could not be picked at all.
 */
const MIN_SECONDS = SECONDS_IN_HOUR;
const MAX_SECONDS = 365 * SECONDS_IN_DAY;

const UNIT_SECONDS = {
  minutes: SECONDS_IN_MINUTE,
  hours: SECONDS_IN_HOUR,
  days: SECONDS_IN_DAY,
} as const;

type UnitKey = keyof typeof UNIT_SECONDS;

const UNIT_LABELS: Record<UnitKey, string> = {
  minutes: 'Common_Minutes',
  hours: 'Common_Hours',
  days: 'Common_Days',
};

const UNIT_COUNT_LABELS: Record<UnitKey, string> = {
  minutes: 'Common_Minute_Count',
  hours: 'Common_Hour_Count',
  days: 'Common_Day_Count',
};

/** What installations actually pick. Anything else is spelled out in the custom row. */
const PRESET_SECONDS = [
  SECONDS_IN_HOUR,
  8 * SECONDS_IN_HOUR,
  SECONDS_IN_DAY,
  7 * SECONDS_IN_DAY,
  30 * SECONDS_IN_DAY,
  90 * SECONDS_IN_DAY,
  365 * SECONDS_IN_DAY,
];

/**
 * A duration has one spelling here: the largest unit that divides it exactly. Months are
 * deliberately not a unit — a month is not a fixed number of days, and treating it as 30
 * is what made "1 month 30 days" and "2 months" two ways of saying the same thing.
 */
const splitDuration = (seconds: number): { amount: number, unit: UnitKey } => {
  if (seconds % SECONDS_IN_DAY === 0) return { amount: seconds / SECONDS_IN_DAY, unit: 'days' };
  if (seconds % SECONDS_IN_HOUR === 0) return { amount: seconds / SECONDS_IN_HOUR, unit: 'hours' };

  return { amount: Math.round(seconds / SECONDS_IN_MINUTE), unit: 'minutes' };
};

const toSeconds = (amount: string, unit: UnitKey): number | null => {
  const parsed = Number(amount);

  if (!amount.trim() || !Number.isInteger(parsed) || parsed <= 0) return null;

  return parsed * UNIT_SECONDS[unit];
};

const isInRange = (seconds: number | null): seconds is number =>
  seconds !== null && seconds >= MIN_SECONDS && seconds <= MAX_SECONDS;

interface SessionExpirationCardProps {
  value: number
  onChanged?(value: number): void
  /** Whether what is on screen can be saved, so the page can gate its save button. */
  onValidityChanged?(valid: boolean): void
}

const SessionExpirationCard: FC<SessionExpirationCardProps> = ({ value, onChanged, onValidityChanged }) => {
  const [isCustom, setIsCustom] = useState(false);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<UnitKey>('days');

  const { t } = useLocalization();

  const { can } = usePermission();
  const hasUpdatePermission = can('server_config:put');

  // What this card last sent upward. The value comes back as a prop, and without this the
  // sync below would answer the user's own typing by rewriting the row they type in.
  const reportedValue = useRef<number | null>(null);

  const isPreset = PRESET_SECONDS.some((preset) => preset === value);
  // `isCustom` catches up with a new value only in the effect below, after the render it
  // arrived in — so a value that is not a preset (the page's 0 before the config loads)
  // would otherwise reach the select as an option it does not have.
  const selection = isCustom ? 'custom' : (isPreset ? String(value) : '');

  useEffect(() => {
    if (reportedValue.current === value) return;

    const { amount: storedAmount, unit: storedUnit } = splitDuration(Math.max(value, 0));

    setIsCustom(!isPreset);
    setAmount(value > 0 ? String(storedAmount) : '');
    setUnit(storedUnit);
  }, [value, isPreset]);

  const customSeconds = toSeconds(amount, unit);
  // A preset is always in range, so only the custom row can be wrong.
  const effectiveSeconds = isCustom ? customSeconds : value;
  const isValid = isInRange(effectiveSeconds);
  /** Something was typed, and it lands outside what the API accepts. */
  const isCustomOutOfRange = isCustom && customSeconds !== null && !isValid;

  useEffect(() => {
    onValidityChanged?.(isValid);
  }, [isValid, onValidityChanged]);

  const report = (seconds: number) => {
    reportedValue.current = seconds;
    onChanged?.(seconds);
  };

  const applyCustom = (nextAmount: string, nextUnit: UnitKey) => {
    setAmount(nextAmount);
    setUnit(nextUnit);

    const seconds = toSeconds(nextAmount, nextUnit);

    // Out of range stays on screen as it was typed, reported as invalid rather than
    // silently rounded to the nearest allowed value the way the old card did.
    if (isInRange(seconds)) report(seconds);
  };

  const onSelectionChanged = (selection: string) => {
    if (selection === 'custom') {
      setIsCustom(true);
      return;
    }

    setIsCustom(false);
    report(Number(selection));
  };

  const formatDuration = (seconds: number) => {
    const { amount: durationAmount, unit: durationUnit } = splitDuration(seconds);

    return t(UNIT_COUNT_LABELS[durationUnit], { count: durationAmount });
  };

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_SessionExpiration')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_SessionExpiration_Description')}</CardDescription>
        </Box>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        <Stack spacing={1.5} alignItems="flex-start">
          {/* The card title labels these fields, so labels of their own would only repeat it. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ width: 220 }}>
              <Select<string>
                value={selection}
                disabled={!hasUpdatePermission}
                onChange={(event) => onSelectionChanged(event.target.value)}
                inputProps={{ 'aria-label': t('ServerConfig_SessionExpiration') }}
              >
                {PRESET_SECONDS.map((preset) => (
                  <MenuItem key={preset} value={String(preset)}>
                    {formatDuration(preset)}
                  </MenuItem>
                ))}
                <MenuItem value="custom">{t('ServerConfig_SessionExpiration_Custom')}</MenuItem>
              </Select>
            </FormControl>

            {isCustom && (
              <>
                <TextField
                  size="small"
                  type="number"
                  value={amount}
                  disabled={!hasUpdatePermission}
                  error={isCustomOutOfRange}
                  onChange={(event) => applyCustom(event.target.value, unit)}
                  slotProps={{ htmlInput: { min: 1, 'aria-label': t('ServerConfig_SessionExpiration') } }}
                  sx={{ width: 110 }}
                />
                <FormControl size="small" sx={{ width: 130 }}>
                  <Select<UnitKey>
                    value={unit}
                    disabled={!hasUpdatePermission}
                    onChange={(event) => applyCustom(amount, event.target.value as UnitKey)}
                    inputProps={{ 'aria-label': t(UNIT_LABELS[unit]) }}
                  >
                    {(Object.keys(UNIT_SECONDS) as UnitKey[]).map((unitKey) => (
                      <MenuItem key={unitKey} value={unitKey}>
                        {t(UNIT_LABELS[unitKey])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>

          {/* On its own line: as the number field's helper text it wrapped to three lines. */}
          {isCustom && (
            <Typography variant="caption" color={isCustomOutOfRange ? 'error' : 'text.secondary'}>
              {t('ServerConfig_SessionExpiration_Range')}
            </Typography>
          )}

          {/* The value in plain words, and the whole card for a reader who cannot edit it. */}
          <Typography variant="body2" color={isValid ? 'text.secondary' : 'warning.main'}>
            {isValid
              ? t('ServerConfig_SessionExpiration_Summary', [formatDuration(effectiveSeconds)])
              : t('ServerConfig_SessionExpiration_NotSet')}
          </Typography>
        </Stack>
      </CardContent>
    </Paper>
  );
};

export default SessionExpirationCard;
